import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Save, Trash2, Plus, X, ChevronUp, ChevronDown, Send, Users } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/app/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AudiencePicker from "@/components/app/AudiencePicker";
import api from "@/lib/api";

const DEFAULT_DRIP = {
  name: "",
  description: "",
  is_active: true,
  audience_type: "all",
  audience_tags: [],
  exclude_tags: [],
  audience_list_ids: [],
  entry_trigger: "manual",
  entry_tag: "",
  steps: [],
};

const ENTRY_LABELS = {
  manual: "ידני (אני אבחר לידים)",
  on_tag_added: "אוטומטי — כשמוסיפים תיוג ספציפי",
};

export default function DripEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [drip, setDrip] = useState(DEFAULT_DRIP);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tpl, t] = await Promise.all([
          api.get("/admin/email/templates"),
          api.get("/admin/tags"),
        ]);
        setTemplates(tpl.data.filter((x) => x.is_active));
        setTags(t.data.map((x) => x.tag));
        if (!isNew) {
          const { data } = await api.get(`/admin/drips/${id}`);
          setDrip({ ...DEFAULT_DRIP, ...data });
          const enr = await api.get(`/admin/drips/${id}/enrollments?limit=100`);
          setEnrollments(enr.data);
        }
      } catch (err) {
        console.error(err);
        toast.error("טעינה נכשלה");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const set = (k, v) => setDrip((d) => ({ ...d, [k]: v }));
  const setStep = (idx, patch) => setDrip((d) => ({
    ...d,
    steps: d.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
  }));
  const addStep = () => setDrip((d) => ({
    ...d,
    steps: [
      ...d.steps,
      {
        name: `שלב ${d.steps.length + 1}`,
        template_id: templates[0]?.id || "",
        delay_days: d.steps.length === 0 ? 0 : (d.steps.length + 1) * 2,
        send_hour_local: "09:00",
        is_active: true,
      },
    ],
  }));
  const removeStep = (idx) => setDrip((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== idx) }));
  const moveStep = (idx, dir) => setDrip((d) => {
    const arr = [...d.steps];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...d, steps: arr };
  });

  const save = async () => {
    if (!drip.name.trim()) return toast.error("שם חובה");
    if (drip.entry_trigger === "on_tag_added" && !(drip.entry_tag || "").trim()) return toast.error("חובה לבחור תיוג טריגר");
    if (drip.steps.length === 0) return toast.error("חובה לפחות שלב אחד");
    for (const s of drip.steps) {
      if (!s.template_id) return toast.error("לכל שלב חובה template");
    }
    setSaving(true);
    try {
      const payload = { ...drip };
      if (isNew) {
        const { data } = await api.post("/admin/drips", payload);
        toast.success("Drip נוצר");
        navigate(`/admin/drips/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/drips/${id}`, payload);
        toast.success("נשמר");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("למחוק דריפ זה? כל השליחות המתוזמנות יבוטלו.")) return;
    try {
      await api.delete(`/admin/drips/${id}`);
      toast.success("נמחק");
      navigate("/admin/drips");
    } catch (err) {
      toast.error("שגיאה במחיקה");
    }
  };

  const enrollAudience = async () => {
    if (!id) return toast.error("שמור לפני שיוך");
    setEnrolling(true);
    try {
      const { data } = await api.post(`/admin/drips/${id}/enroll`, { filter_audience: true });
      toast.success(`שויכו ${data.enrolled} לידים · דולגו ${data.skipped}`);
      const enr = await api.get(`/admin/drips/${id}/enrollments?limit=100`);
      setEnrollments(enr.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "שגיאה בשיוך");
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return <AdminLayout title="טוען..."><div className="py-24 text-center text-stone-500">טוען...</div></AdminLayout>;

  return (
    <AdminLayout title={isNew ? "Drip חדש" : `עריכת דריפ · ${drip.name}`}>
      <div className="flex items-center gap-2 mb-5">
        <Link to="/admin/drips" data-testid="back-to-drips" className="p-2 hover:bg-stone-100 rounded-lg">
          <ArrowRight className="w-5 h-5 text-stone-500" />
        </Link>
        <div className="flex-1" />
        {!isNew && (
          <Button variant="outline" onClick={remove} data-testid="drip-delete-btn" className="h-11 rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <Trash2 className="w-4 h-4 me-2" />מחק
          </Button>
        )}
        <Button onClick={save} disabled={saving} data-testid="drip-save-btn" className="h-11 rounded-full bg-brand-charcoal text-white hover:bg-stone-800">
          <Save className="w-4 h-4 me-2" />{saving ? "שומר..." : "שמור"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Left: settings */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="font-[800] mb-4">פרטים כלליים</h3>
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-bold mb-2 block">שם</Label>
                <Input value={drip.name} onChange={(e) => set("name", e.target.value)} placeholder="למשל: וולקאם סדרה יום 1-5" data-testid="drip-name-input" className="h-11" />
              </div>
              <div>
                <Label className="text-sm font-bold mb-2 block">תיאור פנימי</Label>
                <Textarea value={drip.description} onChange={(e) => set("description", e.target.value)} placeholder="לשימוש פנימי בלבד" data-testid="drip-desc-input" rows={2} />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-bold">פעיל</Label>
                  <p className="text-xs text-stone-500">כשכבוי, שלבים לא נשלחים ולא ניתן לשייך חדשים</p>
                </div>
                <Switch checked={drip.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="drip-active-switch" />
              </div>
            </div>
          </div>

          {/* Entry trigger */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="font-[800] mb-4">איך לידים נכנסים ל-Drip?</h3>
            <div className="space-y-4">
              <Select value={drip.entry_trigger} onValueChange={(v) => set("entry_trigger", v)}>
                <SelectTrigger className="h-11" data-testid="drip-entry-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ENTRY_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`drip-entry-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {drip.entry_trigger === "on_tag_added" && (
                <div>
                  <Label className="text-sm font-bold mb-2 block">תיוג טריגר</Label>
                  <Input
                    value={drip.entry_tag || ""}
                    onChange={(e) => set("entry_tag", e.target.value)}
                    placeholder="למשל: hot_lead"
                    data-testid="drip-entry-tag-input"
                    className="h-11"
                  />
                  <p className="text-xs text-stone-500 mt-1">כאשר תוסיף תיוג זה לליד, הוא ישויך אוטומטית לדריפ</p>
                </div>
              )}
            </div>
          </div>

          {/* Audience */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <h3 className="font-[800] mb-4">קהל היעד (מסננים גם ברגע השליחה)</h3>
            <AudiencePicker
              value={{ audience_type: drip.audience_type, audience_tags: drip.audience_tags, exclude_tags: drip.exclude_tags, audience_list_ids: drip.audience_list_ids || [] }}
              onChange={(v) => setDrip((d) => ({ ...d, ...v }))}
              suggestions={tags}
              testIdPrefix="drip-audience"
            />
          </div>

          {/* Steps */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[800]">שלבים ({drip.steps.length})</h3>
              <Button size="sm" variant="outline" onClick={addStep} data-testid="drip-add-step" className="rounded-full">
                <Plus className="w-4 h-4 me-1" />שלב חדש
              </Button>
            </div>
            {drip.steps.length === 0 ? (
              <div className="text-center py-8 text-sm text-stone-400 border-2 border-dashed border-stone-200 rounded-xl">
                אין שלבים. הוסף לפחות אחד.
              </div>
            ) : (
              <div className="space-y-3">
                {drip.steps.map((s, idx) => (
                  <div key={idx} className="bg-stone-50 rounded-xl p-4 border border-stone-200" data-testid={`drip-step-${idx}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center text-xs font-[800]">{idx + 1}</div>
                      <Input value={s.name} onChange={(e) => setStep(idx, { name: e.target.value })} placeholder="שם שלב פנימי" className="h-9 flex-1" data-testid={`drip-step-${idx}-name`} />
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveStep(idx, -1)} className="p-1.5 rounded-lg hover:bg-stone-200 disabled:opacity-30" disabled={idx === 0}><ChevronUp className="w-4 h-4" /></button>
                        <button type="button" onClick={() => moveStep(idx, 1)} className="p-1.5 rounded-lg hover:bg-stone-200 disabled:opacity-30" disabled={idx === drip.steps.length - 1}><ChevronDown className="w-4 h-4" /></button>
                        <button type="button" onClick={() => removeStep(idx)} data-testid={`drip-step-${idx}-remove`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-stone-500 mb-1 block">Template</Label>
                        <Select value={s.template_id} onValueChange={(v) => setStep(idx, { template_id: v })}>
                          <SelectTrigger className="h-10" data-testid={`drip-step-${idx}-template`}><SelectValue placeholder="בחר..." /></SelectTrigger>
                          <SelectContent>
                            {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500 mb-1 block">תזמון (מההרשמה/שיוך)</Label>
                        {(() => {
                          const hasLegacyOnly = s.delay_days == null && s.delay_minutes != null && s.delay_minutes > 0;
                          const days = s.delay_days ?? (hasLegacyOnly ? Math.round(s.delay_minutes / 1440) : 0);
                          const hour = s.send_hour_local || "09:00";
                          return (
                            <>
                              <div className="grid grid-cols-2 gap-2">
                                <Input
                                  type="number"
                                  min={0}
                                  max={365}
                                  value={days}
                                  onChange={(e) => setStep(idx, {
                                    delay_days: Math.max(0, Number(e.target.value || 0)),
                                    send_hour_local: hour,
                                    delay_minutes: 0,
                                  })}
                                  className="h-10"
                                  data-testid={`drip-step-${idx}-days`}
                                  placeholder="ימים"
                                />
                                <Input
                                  type="time"
                                  value={hour}
                                  onChange={(e) => setStep(idx, {
                                    delay_days: days,
                                    send_hour_local: e.target.value || "09:00",
                                    delay_minutes: 0,
                                  })}
                                  className="h-10 ltr text-left"
                                  data-testid={`drip-step-${idx}-hour`}
                                />
                              </div>
                              <div className="text-[10px] text-stone-400 mt-1">
                                {days === 0 ? "ביום השיוך" : `${days} ${days === 1 ? "יום" : "ימים"} מהשיוך`}, בשעה {hour} (שעון ישראל).
                                {hasLegacyOnly && (
                                  <div className="text-orange-600 mt-0.5">⚠ שלב ישן ({s.delay_minutes} דק&#39;) — מומר אוטומטית ל-{days} ימים @ {hour}.</div>
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-stone-500">שלב פעיל</span>
                      <Switch checked={s.is_active !== false} onCheckedChange={(v) => setStep(idx, { is_active: v })} data-testid={`drip-step-${idx}-active`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: enrollments & actions */}
        <div className="space-y-5">
          {!isNew && (
            <div className="bg-white rounded-2xl p-6 border border-stone-200 sticky top-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5 text-brand-amber" />
                <h3 className="font-[800]">שיוך לידים</h3>
              </div>
              <p className="text-xs text-stone-500 leading-relaxed mb-4">
                לחיצה תשייך את כל הלידים שתואמים לקהל היעד. לידים שכבר שויכו יידלגו אוטומטית.
              </p>
              <Button onClick={enrollAudience} disabled={enrolling || !drip.is_active} className="w-full h-11 rounded-full bg-brand-amber hover:bg-amber-600 text-white" data-testid="drip-enroll-btn">
                <Send className="w-4 h-4 me-2" />{enrolling ? "משייך..." : "שייך לפי קהל"}
              </Button>

              <div className="border-t border-stone-100 mt-5 pt-4">
                <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">רשומים אחרונים ({enrollments.length})</div>
                {enrollments.length === 0 ? (
                  <div className="text-xs text-stone-400 text-center py-4">אין רשומים</div>
                ) : (
                  <div className="max-h-72 overflow-y-auto space-y-2">
                    {enrollments.slice(0, 20).map((e) => (
                      <div key={e.id} className="text-xs flex items-center justify-between bg-stone-50 rounded-lg px-2.5 py-1.5">
                        <span className="font-bold text-stone-700 truncate">{e.lead_id.replace(/^lead_/, "")}</span>
                        <span className={`px-1.5 py-0.5 rounded ${
                          e.status === "active" ? "bg-amber-100 text-brand-amber" :
                          e.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                          "bg-stone-100 text-stone-500"
                        }`}>
                          {statusLabel(e.status)} · {e.current_step_index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function statusLabel(s) {
  return {
    active: "פעיל",
    completed: "הושלם",
    excluded: "הוצא",
    unsubscribed: "הוסר",
  }[s] || s;
}
